import clsx from 'clsx'
import { nothing, PropertyValueMap, unsafeCSS } from 'lit'
import { property, query } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html, unsafeStatic } from 'lit/static-html.js'

import { GenericFormElement } from '@/internal/mixin/genericFormElement'
import { t } from '@/localization/localization'

import {
  ButtonShape,
  ButtonVariant,
  HtmlButtonType,
  LinkTarget,
} from '../../common/types'
import DapDSIcon from '../icon/icon.component'
import { ButtonSize } from '../icon-button/icon-button.component'
import SystemLoadingSpinner from '../icons/system/system-loading-spinner/system-loading-spinner.component'
import styles from './_styles/button.scss?inline'
import { buttonVariants } from './button.variants'

const LOADING_ICON_SIZES = {
  lg: 24,
  md: 20,
  sm: 16,
  xs: 16,
} as const

/**
 * `dap-ds-button` is a versatile button component for triggering actions and navigation.
 * @element dap-ds-button
 * @summary A button is a clickable element that can be used to trigger an action, submit forms, or navigate to other pages.
 * @title - Button
 *
 * @example Basic Usage
 * ```html
 * <dap-ds-button>Click me</dap-ds-button>
 * <dap-ds-button variant="outline">Secondary action</dap-ds-button>
 * <dap-ds-button href="/dashboard">Navigate</dap-ds-button>
 * ```
 *
 * @example Form Integration
 * ```html
 * <form>
 *   <dap-ds-button htmlType="submit">Submit Form</dap-ds-button>
 *   <dap-ds-button htmlType="reset" variant="outline">Reset</dap-ds-button>
 * </form>
 * ```
 *
 * @example Accessibility
 * ```html
 * <!-- Icon-only buttons require aria-label -->
 * <dap-ds-button aria-label="Close dialog" shape="circle">
 *   <dap-ds-icon name="close-line"></dap-ds-icon>
 * </dap-ds-button>
 * ```
 *
 * @property {string} sizeMap - The size map of the button.
 * @example
 * ```html
 * <dap-ds-button sizeMap="sm:lg,xs:xs">Small</dap-ds-button>
 * ```
 *
 * @event dds-loading-timeout - Emitted when the loading timeout is reached
 *
 * @slot - The content of the button. Can contain text, icons, or other elements.
 *
 * @csspart base - The main button container.
 * @csspart high-contrast - The high contrast part of the button.
 * @csspart content - The content wrapper inside the button.
 *
 * @cssproperty --dds-button-padding-x - Horizontal padding of the button (default: var(--dds-spacing-300))
 * @cssproperty --dds-button-padding-y - Vertical padding of the button (default: var(--dds-spacing-300))
 * @cssproperty --dds-button-border-radius - Border radius of the button (default: var(--dds-radius-rounded))
 * @cssproperty --dds-button-font-weight - Font weight of the button (default: 700)
 * @cssproperty --dds-button-line-height - Line height of the button (default: var(--dds-font-line-height-large))
 * @cssproperty --dds-button-transition - Transition property of the button (default: all 0.2s ease-in-out)
 * @cssproperty --dds-button-disabled-opacity - Opacity of disabled button (default: 0.5)
 *
 * @cssproperty --dds-button-size-lg - Size of large button (default: var(--dds-spacing-1200))
 * @cssproperty --dds-button-size-md - Size of medium button (default: var(--dds-spacing-1000))
 * @cssproperty --dds-button-size-sm - Size of small button (default: var(--dds-spacing-800))
 * @cssproperty --dds-button-size-xs - Size of extra small button (default: var(--dds-spacing-600))
 *
 * @cssproperty --dds-button-circle-lg - Size of large circle button (default: var(--dds-spacing-1200))
 * @cssproperty --dds-button-circle-md - Size of medium circle button (default: var(--dds-spacing-1000))
 * @cssproperty --dds-button-circle-sm - Size of small circle button (default: var(--dds-spacing-800))
 * @cssproperty --dds-button-circle-xs - Size of extra small circle button (default: var(--dds-spacing-600))
 *
 * Primary button properties:
 * @cssproperty --dds-button-primary-color-bg - Background color of primary button (default: var(--dds-button-primary-background-enabled))
 * @cssproperty --dds-button-primary-color-bg-hover - Background color of primary button on hover (default: var(--dds-button-primary-background-hover))
 * @cssproperty --dds-button-primary-color-bg-active - Background color of primary button when active (default: var(--dds-button-primary-background-pressed))
 * @cssproperty --dds-button-primary-color-bg-disabled - Background color of disabled primary button (default: var(--dds-button-primary-background-disabled))
 * @cssproperty --dds-button-primary-color-text - Text color of primary button (default: var(--dds-button-primary-text-enabled))
 * @cssproperty --dds-button-primary-color-text-disabled - Text color of disabled primary button (default: var(--dds-button-primary-text-disabled))
 *
 * Primary inverted button properties:
 * @cssproperty --dds-button-primary-inverted-color-bg - Background color of primary inverted button (default: var(--dds-button-primary-background-inverted-enabled))
 * @cssproperty --dds-button-primary-inverted-color-bg-hover - Background color of primary inverted button on hover (default: var(--dds-button-primary-background-inverted-hover))
 * @cssproperty --dds-button-primary-inverted-color-bg-active - Background color of primary inverted button when active (default: var(--dds-button-primary-background-inverted-pressed))
 * @cssproperty --dds-button-primary-inverted-color-bg-disabled - Background color of disabled primary inverted button (default: var(--dds-button-primary-background-inverted-disabled))
 * @cssproperty --dds-button-primary-inverted-color-text - Text color of primary inverted button (default: var(--dds-button-primary-text-inverted))
 * @cssproperty --dds-button-primary-inverted-color-text-disabled - Text color of disabled primary inverted button (default: var(--dds-button-primary-text-inverted-disabled))
 *
 * Outline button properties:
 * @cssproperty --dds-button-outline-color-border - Border color of outline button (default: var(--dds-button-outline-border-enabled))
 * @cssproperty --dds-button-outline-color-border-hover - Border color of outline button on hover (default: var(--dds-button-outline-border-hover))
 * @cssproperty --dds-button-outline-color-border-active - Border color of outline button when active (default: var(--dds-button-outline-border-pressed))
 * @cssproperty --dds-button-outline-color-border-disabled - Border color of disabled outline button (default: var(--dds-button-outline-border-disabled))
 * @cssproperty --dds-button-outline-color-text - Text color of outline button (default: var(--dds-button-outline-text-enabled))
 * @cssproperty --dds-button-outline-color-text-hover - Text color of outline button on hover (default: var(--dds-button-outline-text-hover))
 * @cssproperty --dds-button-outline-color-text-active - Text color of outline button when active (default: var(--dds-button-outline-text-pressed))
 * @cssproperty --dds-button-outline-color-text-disabled - Text color of disabled outline button (default: var(--dds-button-outline-text-disabled))
 *
 * Outline inverted button properties:
 * @cssproperty --dds-button-outline-inverted-color-border - Border color of outline inverted button (default: var(--dds-button-outline-border-inverted-enabled))
 * @cssproperty --dds-button-outline-inverted-color-border-hover - Border color of outline inverted button on hover (default: var(--dds-button-outline-border-inverted-hover))
 * @cssproperty --dds-button-outline-inverted-color-border-active - Border color of outline inverted button when active (default: var(--dds-button-outline-border-inverted-pressed))
 * @cssproperty --dds-button-outline-inverted-color-border-disabled - Border color of disabled outline inverted button (default: var(--dds-button-outline-border-inverted-disabled))
 * @cssproperty --dds-button-outline-inverted-color-text - Text color of outline inverted button (default: var(--dds-button-outline-text-inverted-enabled))
 * @cssproperty --dds-button-outline-inverted-color-text-hover - Text color of outline inverted button on hover (default: var(--dds-button-outline-text-inverted-hover))
 * @cssproperty --dds-button-outline-inverted-color-text-active - Text color of outline inverted button when active (default: var(--dds-button-outline-text-inverted-pressed))
 * @cssproperty --dds-button-outline-inverted-color-text-disabled - Text color of disabled outline inverted button (default: var(--dds-button-outline-text-inverted-disabled))
 *
 * Subtle button properties:
 * @cssproperty --dds-button-subtle-color-bg - Background color of subtle button (default: var(--dds-button-subtle-background-enabled))
 * @cssproperty --dds-button-subtle-color-bg-hover - Background color of subtle button on hover (default: var(--dds-button-subtle-background-hover))
 * @cssproperty --dds-button-subtle-color-bg-active - Background color of subtle button when active (default: var(--dds-button-subtle-background-pressed))
 * @cssproperty --dds-button-subtle-color-bg-disabled - Background color of disabled subtle button (default: var(--dds-button-subtle-background-disabled))
 * @cssproperty --dds-button-subtle-color-border - Border color of subtle button (default: var(--dds-button-subtle-border-enabled))
 * @cssproperty --dds-button-subtle-color-border-hover - Border color of subtle button on hover (default: var(--dds-button-subtle-border-hover))
 * @cssproperty --dds-button-subtle-color-border-active - Border color of subtle button when active (default: var(--dds-button-subtle-border-pressed))
 * @cssproperty --dds-button-subtle-color-border-disabled - Border color of disabled subtle button (default: var(--dds-button-subtle-border-disabled))
 * @cssproperty --dds-button-subtle-color-text - Text color of subtle button (default: var(--dds-button-subtle-text-enabled))
 * @cssproperty --dds-button-subtle-color-text-hover - Text color of subtle button on hover (default: var(--dds-button-subtle-text-hover))
 * @cssproperty --dds-button-subtle-color-text-active - Text color of subtle button when active (default: var(--dds-button-subtle-text-pressed))
 * @cssproperty --dds-button-subtle-color-text-disabled - Text color of disabled subtle button (default: var(--dds-button-subtle-text-disabled))
 *
 * Clean button properties:
 * @cssproperty --dds-button-clean-color-text - Text color of clean button (default: var(--dds-text-link-base))
 * @cssproperty --dds-button-clean-color-text-hover - Text color of clean button on hover (default: var(--dds-text-link-hover))
 * @cssproperty --dds-button-clean-color-text-active - Text color of clean button when active (default: var(--dds-text-link-pressed))
 * @cssproperty --dds-button-clean-color-text-disabled - Text color of disabled clean button (default: var(--dds-text-neutral-disabled))
 *
 * Clean inverted button properties:
 * @cssproperty --dds-button-clean-inverted-color-text - Text color of clean inverted button (default: var(--dds-button-primary-text-inverted))
 * @cssproperty --dds-button-clean-inverted-color-text-hover - Text color of clean inverted button on hover (default: var(--dds-button-primary-text-inverted))
 * @cssproperty --dds-button-clean-inverted-color-text-active - Text color of clean inverted button when active (default: var(--dds-button-primary-text-inverted))
 * @cssproperty --dds-button-clean-inverted-color-text-disabled - Text color of disabled clean inverted button (default: var(--dds-text-neutral-disabled))
 *
 * Danger button properties:
 * @cssproperty --dds-button-danger-color-bg - Background color of danger button (default: var(--dds-button-primary-background-destructive-enabled))
 * @cssproperty --dds-button-danger-color-bg-hover - Background color of danger button on hover (default: var(--dds-button-primary-background-destructive-hover))
 * @cssproperty --dds-button-danger-color-bg-active - Background color of danger button when active (default: var(--dds-button-primary-background-destructive-pressed))
 * @cssproperty --dds-button-danger-color-text - Text color of danger button (default: var(--dds-button-primary-text-enabled))
 * @cssproperty --dds-button-danger-outline-color-border - Border color of danger outline button (default: var(--dds-button-outline-border-destructive-enabled))
 * @cssproperty --dds-button-danger-outline-color-border-hover - Border color of danger outline button on hover (default: var(--dds-button-outline-border-destructive-hover))
 * @cssproperty --dds-button-danger-outline-color-border-active - Border color of danger outline button when active (default: var(--dds-button-outline-border-destructive-pressed))
 * @cssproperty --dds-button-danger-outline-color-text - Text color of danger outline button (default: var(--dds-button-outline-text-destructive-enabled))
 * @cssproperty --dds-button-danger-outline-color-text-hover - Text color of danger outline button on hover (default: var(--dds-button-outline-text-destructive-hover))
 * @cssproperty --dds-button-danger-outline-color-text-active - Text color of danger outline button when active (default: var(--dds-button-outline-text-destructive-pressed))
 * @cssproperty --dds-button-danger-subtle-color-bg - Background color of danger subtle button (default: var(--dds-button-subtle-background-destructive-enabled))
 * @cssproperty --dds-button-danger-subtle-color-bg-hover - Background color of danger subtle button on hover (default: var(--dds-button-subtle-background-destructive-hover))
 * @cssproperty --dds-button-danger-subtle-color-bg-active - Background color of danger subtle button when active (default: var(--dds-button-subtle-background-destructive-pressed))
 * @cssproperty --dds-button-danger-subtle-color-text - Text color of danger subtle button (default: var(--dds-button-subtle-text-destructive-enabled))
 * @cssproperty --dds-button-danger-subtle-color-text-hover - Text color of danger subtle button on hover (default: var(--dds-button-subtle-text-destructive-hover))
 * @cssproperty --dds-button-danger-subtle-color-text-active - Text color of danger subtle button when active (default: var(--dds-button-subtle-text-destructive-pressed))
 * @cssproperty --dds-button-danger-clean-color-text - Text color of danger clean button (default: var(--dds-text-negative-subtle))
 * @cssproperty --dds-button-danger-clean-color-text-hover - Text color of danger clean button on hover (default: var(--dds-text-negative-base))
 * @cssproperty --dds-button-danger-clean-color-text-active - Text color of danger clean button when active (default: var(--dds-text-negative-strong))
 */
export default class DapDSButton extends GenericFormElement {
  static override tagName = 'dap-ds-button'

  /** Components rendered inside the button, auto-registered on first use. */
  static override dependencies = {
    'dap-ds-icon': DapDSIcon,
    'dap-ds-icon-loading-spinner': SystemLoadingSpinner,
  }

  /**
   * @ignore
   */
  @query('.button') readonly button!: HTMLButtonElement | HTMLLinkElement

  /** The visual style variant of the button
   * @type { 'primary' | 'outline' | 'subtle' | 'subtle-neutral' | 'subtle-quiet' | 'subtle-quiet-inverted' | 'clean' | 'primary-inverted' | 'outline-inverted' | 'subtle-inverted' | 'clean-inverted' | 'subtle-menu' | 'subtle-menu-item' }
   * @default 'primary'
   * @example
   * ```html
   * <dap-ds-button variant="primary">Primary action</dap-ds-button>
   * <dap-ds-button variant="outline">Secondary action</dap-ds-button>
   * <dap-ds-button variant="subtle">Tertiary action</dap-ds-button>
   * ```
   * @group Visual
   */
  @property({ reflect: true }) variant: ButtonVariant = 'primary'

  /** The size of the button affecting padding and font size
   * @type { 'lg' | 'md' | 'sm' | 'xs' }
   * @default 'md'
   * @example
   * ```html
   * <dap-ds-button size="xs">Extra small</dap-ds-button>
   * <dap-ds-button size="sm">Small</dap-ds-button>
   * <dap-ds-button>Medium (default)</dap-ds-button>
   * <dap-ds-button size="lg">Large</dap-ds-button>
   * ```
   * @group Visual
   */
  @property({ reflect: true }) size: ButtonSize = 'md'

  /** Whether the button is in loading state, showing a spinner and disabling interaction
   * @default false
   * @example
   * ```html
   * <dap-ds-button loading>Submitting...</dap-ds-button>
   * ```
   * @group State
   */
  @property({ type: Boolean, reflect: true }) loading = false

  /** Whether the button represents a destructive action (applies danger styling)
   * @default false
   * @example
   * ```html
   * <dap-ds-button danger>Delete Account</dap-ds-button>
   * <dap-ds-button variant="outline" danger>Remove Item</dap-ds-button>
   * ```
   * @group State
   */
  @property({ type: Boolean }) danger = false

  /** The shape of the button - use 'circle' for icon-only buttons
   * @type { 'button' | 'circle' }
   * @default 'button'
   * @example
   * ```html
   * <dap-ds-button shape="circle" aria-label="Close">
   *   <dap-ds-icon name="close-line"></dap-ds-icon>
   * </dap-ds-button>
   * ```
   * @group Visual
   */
  @property({ reflect: true }) shape: ButtonShape = 'button'

  /** The HTML type attribute for form interaction
   * @type { 'button' | 'submit' | 'reset' }
   * @default 'button'
   * @example
   * ```html
   * <form>
   *   <dap-ds-button htmlType="submit">Submit</dap-ds-button>
   *   <dap-ds-button htmlType="reset">Reset</dap-ds-button>
   * </form>
   * ```
   * @group Form
   */
  @property() htmlType: HtmlButtonType = 'button'

  /** The URL to navigate to. When present, the button renders as an anchor element
   * @example
   * ```html
   * <dap-ds-button href="/dashboard">Go to Dashboard</dap-ds-button>
   * <dap-ds-button href="https://example.com" target="_blank">External Link</dap-ds-button>
   * ```
   * @group Navigation
   */
  @property() href?: string

  /** The target attribute for link navigation
   * @type { '_blank' | '_self' | '_parent' | '_top' }
   * @default '_self'
   * @example
   * ```html
   * <dap-ds-button href="https://example.com" target="_blank">Open in new tab</dap-ds-button>
   * ```
   * @group Navigation
   */
  @property() target: LinkTarget = '_self'

  /** The rel attribute for link security and behavior
   * @default 'noreferrer noopener'
   * @example
   * ```html
   * <dap-ds-button href="https://example.com" rel="noopener">Safe external link</dap-ds-button>
   * ```
   * @group Navigation
   */
  @property() rel?: string

  /** Controls loading timeout in milliseconds
   * @default 0
   * @group State
   */
  @property({ type: Number }) loadingTimeout = 0

  /** Enables high contrast theme toggle functionality (internal use)
   * @default false
   * @ignore
   */
  @property({ type: Boolean }) hc: boolean = false

  /** Whether the button is active
   * @default false
   * @example
   * ```html
   * <dap-ds-button active>Active</dap-ds-button>
   * ```
   */
  @property({ type: Boolean }) active: boolean = false

  /**
   * Enable OKLCH proof-of-concept theming (experimental)
   * @default false
   * @internal
   * @ignore
   */
  @property({ type: Boolean, attribute: 'data-oklch-poc', reflect: true })
  oklchPoc: boolean = false

  /** The text for the target blank link, applicable when target is _blank. PRO TIP: Use parenthesis to wrap the text */
  @property({ type: String }) targetBlankText: string

  /** Overrides the ARIA role on the internal native button/anchor element. Use when the host's semantics must differ from the native element (e.g. role="link" on a button acting as a SPA navigation trigger). */
  @property({ type: String }) forceRole?: string

  static readonly styles = unsafeCSS(styles)

  private loadingTimer?: number

  /** Persisted aria-label for the inner button; host attribute is removed so we keep the value here */
  private _ariaLabelForButton: string | null = null

  /** Internal flag to ignore the aria-label change triggered by our own attribute cleanup */
  private _ignoreNextAriaLabelChange = false

  private getSecureRel(): string {
    if (this.target === '_blank') {
      return this.rel ?? 'noreferrer noopener'
    }
    return this.rel ?? ''
  }

  private validateAriaPressed(): string | undefined | null {
    if (
      this.ariaPressed &&
      !['true', 'false', 'mixed'].includes(this.ariaPressed)
    ) {
      console.warn('aria-pressed must be "true", "false", or "mixed"')
      return undefined
    }

    return this.ariaPressed
  }

  private getAriaExpanded(): string | null | undefined {
    if (this.ariaExpanded && !['true', 'false'].includes(this.ariaExpanded)) {
      console.warn('aria-expanded must be "true" or "false"')
      return undefined
    }
    return this.ariaExpanded
  }

  private validateProperties(): void {
    if (
      this.shape === 'circle' &&
      !this.effectiveAriaLabel &&
      !this.textContent?.trim() &&
      !this.ariaHidden
    ) {
      console.warn(
        'Circle buttons require aria-label or visible text for accessibility',
      )
    }

    if (this.href && this.htmlType !== 'button') {
      console.warn(
        'htmlType is ignored when href is provided - button will render as anchor',
      )
    }

    if (this.href && !this.isValidUrl(this.href)) {
      console.warn('Invalid URL provided for href attribute')
    }
  }

  private isValidUrl(url: string): boolean {
    try {
      if (
        url.startsWith('/') ||
        url.startsWith('./') ||
        url.startsWith('../')
      ) {
        return true
      }
      new URL(url)
      return true
    } catch {
      return false
    }
  }

  private handleClick(event: Event): boolean {
    if (this.disabled || this.loading) {
      // Returning false does not cancel a Lit listener, and an anchor has no
      // native disabled state, so without this a disabled href button navigates.
      event.preventDefault()
      event.stopPropagation()
      return false
    }

    let handled = false
    if (this.htmlType !== 'button') {
      const proxy = document.createElement('button')
      proxy.type = this.htmlType
      this.insertAdjacentElement('afterend', proxy)

      proxy.click()
      proxy.remove()
      handled = true
    }

    if (this.hc) {
      this.toggleHighContrast()
    }

    return handled
  }

  private handleKeyDown(event: KeyboardEvent): void {
    // Only handle keyboard events for anchor elements that act as buttons
    if (!this.href) {
      return
    }

    // WAI-ARIA: Elements acting as buttons must respond to Enter and Space keys
    if (event.key === ' ' || event.key === 'Enter') {
      if (this.disabled || this.loading) {
        event.preventDefault()
        event.stopPropagation()
        return
      }

      // For Space key: prevent default scrolling behavior and trigger click
      if (event.key === ' ') {
        event.preventDefault()
        event.stopPropagation()
        this.button.click()
      }
    }
  }

  private toggleHighContrast(): void {
    document.body.classList.toggle('high-contrast-theme')
  }

  private startLoadingTimeout(): void {
    if (this.loadingTimeout > 0) {
      this.loadingTimer = window.setTimeout(() => {
        this.loading = false
        this.emit(
          'dds-loading-timeout',
          {
            timeout: this.loadingTimeout,
          },
          {
            bubbles: true,
          },
        )
      }, this.loadingTimeout)
    }
  }

  private clearLoadingTimeout(): void {
    if (this.loadingTimer) {
      clearTimeout(this.loadingTimer)
      this.loadingTimer = undefined
    }
  }

  /**
   * `this.button` is a `@query`, so it is null until Lit has rendered the shadow root.
   * A caller focusing a button the same tick it mounts -- a "Save" button that appears
   * only after the form it belongs to opens, say -- would otherwise get a TypeError,
   * not a no-op. Throwing out of a focus() call breaks whatever effect made it.
   */
  focus(options?: FocusOptions) {
    this.button?.focus(options)
  }

  blur() {
    this.button?.blur()
  }

  /**
   * @ignore
   */
  public get focusElement() {
    return this.button
  }

  /** Aria-label for the inner button; survives host attribute removal so Lit does not clear it */
  private get effectiveAriaLabel(): string | null {
    return this.ariaLabel ?? this._ariaLabelForButton
  }

  protected updated(
    changedProperties: Map<string | number | symbol, unknown>,
  ): void {
    super.updated(changedProperties)

    if (changedProperties.has('ariaLabel')) {
      const newValue = this.ariaLabel

      if (newValue != null && newValue !== '') {
        // Persist value before removing host attribute so the inner button keeps it
        this._ariaLabelForButton = newValue
      } else if (!this._ignoreNextAriaLabelChange) {
        // Only clear when the consumer explicitly removes/clears aria-label
        this._ariaLabelForButton = null
      }

      // Reset the guard after handling the change
      this._ignoreNextAriaLabelChange = false
    }

    if (this.ariaLabel != null && this.ariaLabel !== '') {
      // Removing the host attribute will trigger another ariaLabel change,
      // which we intentionally ignore so the inner button keeps its label.
      this._ignoreNextAriaLabelChange = true
      this.removeAttribute('aria-label')
    }

    if (changedProperties.has('loading')) {
      if (this.loading) {
        this.startLoadingTimeout()
      } else {
        this.clearLoadingTimeout()
      }
    }
  }

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.firstUpdated(_changedProperties)
    this.validateProperties()
  }

  disconnectedCallback(): void {
    super.disconnectedCallback()
    this.clearLoadingTimeout()
  }

  render() {
    return html`
      <${unsafeStatic(this.href ? 'a' : 'button')}
        part="base"
        .disabled=${this.disabled}
        aria-disabled=${this.disabled}
        aria-hidden=${ifDefined(this.ariaHidden)}
        aria-expanded=${ifDefined(this.getAriaExpanded())}
        aria-controls=${ifDefined(this.ariaControls)}
        aria-label=${ifDefined(this.effectiveAriaLabel)}
        aria-haspopup=${ifDefined(this.ariaHasPopup)}
        aria-describedby=${ifDefined(this.ariaDescribedBy)}
        aria-pressed=${ifDefined(this.validateAriaPressed())}
        role=${ifDefined(this.forceRole)}
        .autofocus=${this.autofocus}
        tabindex=${ifDefined(this.href && this.disabled ? '-1' : undefined)}
        type=${ifDefined(this.href ? null : this.htmlType)}
        name=${ifDefined(this.name)}
        href=${ifDefined(this.href)}
        target=${ifDefined(this.target)}
        rel=${ifDefined(this.getSecureRel())}
        class=${clsx(
          buttonVariants({
            intent: this.variant,
            size: this.effectiveSize as ButtonSize,
            shape: this.shape,
          }),
          {
            'dds-button--danger': this.danger,
            'dds-button--loading': this.loading,
            'dds-button--active': this.active,
            // An anchor never reflects a disabled attribute, so the [disabled]
            // styling cannot reach it. Style off the class, as link--disabled does.
            'button--disabled': this.disabled,
          },
        )}
        @click=${this.handleClick}
        @keydown=${this.handleKeyDown}>
        <div part="high-contrast" class="button__highcontrast"></div>
        ${
          this.loading
            ? this._getLoadingSpinner()
            : html` <span part="content" class="button__text">
                <slot></slot>
              </span>`
        }
        ${
          this.target === '_blank'
            ? html`<span class="sr-only">
                ${this.targetBlankText ?? t('opensInNewTab')}</span
              >`
            : nothing
        }
      </${unsafeStatic(this.href ? 'a' : 'button')}>
    `
  }

  private getLoadingIconSize(size: ButtonSize): number {
    return LOADING_ICON_SIZES[size] || LOADING_ICON_SIZES.md
  }

  private _getLoadingSpinner() {
    return html`
      <span class="button__spinner" aria-live="polite">
        <dap-ds-icon-loading-spinner
          size=${this.getLoadingIconSize(this.effectiveSize as ButtonSize)}
          aria-label=${t('loading')}></dap-ds-icon-loading-spinner>
      </span>
    `
  }
}
